import React, { useEffect, useMemo, useState } from "react";
import { get, post } from "../../services/api";
import { MaterialReactTable } from "material-react-table";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import FullScreenLoader from "../../components/FullScreenLoader";

function FranchiseList() {
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const organizationId = localStorage.getItem("organizationId");
  const navigate = useNavigate();

  // Define the column structure for franchises
  const columnLabels = {
    franchiseCode: "Franchise Code",
    franchiseName: "Franchise Name",
    city: "City",
    campusCode: "Campus Code",
    campusName: "Campus Name",
    schoolCategory: "School Category",
    status: "Status",
    overdueAmount: "Overdue Amount",
    royaltyType: "Royalty Type",
    totalFee: "Total Fee",
    remainingBalance: "Remaining Fee",
  };

  const [franchisesData, setFranchisesData] = useState([]);
  const [tablePreferences, setTablePreferences] = useState(null);
  const [columnOrder, setColumnOrder] = useState([
    "franchiseCode",
    "franchiseName",
    "city",
    "campusCode",
    "campusName",
    "schoolCategory",
    "status",
    "overdueAmount",
    "royaltyType",
    "totalFee",
    "remainingBalance",
  ]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState([]);
  const [columnSizing, setColumnSizing] = useState({});
  const [filterModes, setFilterModes] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(false);

  const fetchFranchises = async () => {
    try {
      const response = await get(`Franchise/list?id=${organizationId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (Array.isArray(response)) {
        setFranchisesData(response);
      } else if (response.isSuccess && response.data) {
        setFranchisesData(response.data);
      } else {
        setFranchisesData(response || []);
      }
    } catch (error) {
      console.error("Error fetching franchises:", error);
      toast.error("Failed to fetch franchises");
      setFranchisesData([]);
    }
  };

  const initializeDefaultPreferences = () => {
    const defaultFilterModes = Object.fromEntries(
      columnOrder.map((key) => [
        key,
        key.includes("Code") ||
        key.includes("Amount") ||
        key.includes("Fee") ||
        key.includes("Balance")
          ? "equals"
          : key.includes("Date")
          ? "between"
          : "contains",
      ])
    );

    const defaultColumnSizing = Object.fromEntries(
      columnOrder.map((key) => [key, 150])
    );

    setFilterModes(defaultFilterModes);
    setColumnSizing(defaultColumnSizing);
    setTablePreferences({
      columnOrder,
      columnVisibility: {},
      columnSortability: Object.fromEntries(
        columnOrder.map((key) => [key, true])
      ),
      columnSorting: [],
      columnFilterability: Object.fromEntries(
        columnOrder.map((key) => [key, true])
      ),
      columnWidths: defaultColumnSizing,
      columnFilterModes: defaultFilterModes,
    });
  };

  const saveTablePreferences = () => {
    // Store preferences in localStorage for persistence across sessions
    const preferences = {
      columnOrder: columnOrder.filter(
        (key) => key !== "mrt-row-spacer" && key !== "actions"
      ),
      columnVisibility: Object.fromEntries(
        Object.entries(columnVisibility).filter(
          ([key]) => key !== "mrt-row-spacer" && key !== "actions"
        )
      ),
      sorting: sorting.filter((s) => s.id !== "actions"),
      columnSizing: Object.fromEntries(
        Object.entries(columnSizing)
          .filter(([key]) => key !== "mrt-row-spacer" && key !== "actions")
          .map(([key, value]) => [key, Math.max(value, 50)])
      ),
      filterModes: Object.fromEntries(
        Object.entries(filterModes).filter(
          ([key]) => key !== "mrt-row-spacer" && key !== "actions"
        )
      ),
    };

    localStorage.setItem(
      `franchiseTable_${userId}`,
      JSON.stringify(preferences)
    );
    toast.success("Table preferences saved locally");
  };

  const loadSavedPreferences = () => {
    try {
      const savedPreferences = localStorage.getItem(`franchiseTable_${userId}`);
      if (savedPreferences) {
        const preferences = JSON.parse(savedPreferences);

        setColumnOrder(preferences.columnOrder || columnOrder);
        setColumnVisibility(preferences.columnVisibility || {});
        setSorting(preferences.sorting || []);
        setColumnSizing(preferences.columnSizing || {});
        setFilterModes(preferences.filterModes || {});

        toast.info("Loaded saved table preferences");
      } else {
        initializeDefaultPreferences();
      }
    } catch (error) {
      console.error("Error loading saved preferences:", error);
      initializeDefaultPreferences();
    }
  };

  const fetchData = async () => {
    setLoading(true);
    await fetchFranchises();
    loadSavedPreferences();
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (franchiseId) => {
    navigate(`/franchise?mode=edit&franchiseId=${franchiseId}`);
  };

  const handleAdd = () => {
    navigate("/franchise?mode=new");
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this franchise?"
    );
    if (!confirmed) return;

    try {
      const response = await get(`Franchise/Delete?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.isSuccess) {
        toast.success(response.message || "Franchise deleted successfully!");
        fetchData();
      } else {
        toast.error(response?.message || "An error occurred while deleting.");
      }
    } catch (error) {
      console.error("Error deleting franchise:", error);
      toast.error("An error occurred while deleting the franchise.");
    }
  };

  const handleRowClick = (row) => {
    handleEdit(row.original.id);
  };

  const columns = useMemo(
    () => [
      ...columnOrder
        .filter((key) => key !== "mrt-row-spacer" && key !== "actions")
        .map((key) => ({
          accessorKey: key,
          id: key,
          header: columnLabels[key] || key,
          filterVariant: "text",
          enableColumnFilter: true,
          enableColumnFilterModes: true,
          filterModeOptions: [
            "contains",
            "startsWith",
            "endsWith",
            "equals",
            "notEquals",
            "greaterThan",
            "lessThan",
            "greaterThanOrEqualTo",
            "lessThanOrEqualTo",
            "between",
            "empty",
            "notEmpty",
          ],
          filterFn:
            filterModes[key] ||
            (key.includes("Code") ||
            key.includes("Amount") ||
            key.includes("Fee") ||
            key.includes("Balance")
              ? "equals"
              : key.includes("Date")
              ? "between"
              : "contains"),
          enableSorting: true,
          size: columnSizing[key] || 150,
          minSize: 50,
          maxSize: 1000,
          // Custom cell rendering for specific columns
          Cell: ({ cell }) => {
            const value = cell.getValue();
            if (
              key === "overdueAmount" ||
              key === "totalFee" ||
              key === "remainingBalance"
            ) {
              return typeof value === "number"
                ? Math.round(value).toLocaleString()
                : value;
            }
            if (key === "status") {
              const statusColors = {
                Draft: "badge bg-secondary",
                "Pending Approval": "badge bg-warning",
                Approved: "badge bg-success",
                Rejected: "badge bg-danger",
              };
              return (
                <span
                  className={statusColors[value] || "badge bg-light text-dark"}
                >
                  {value}
                </span>
              );
            }
            return value;
          },
        })),
      {
        accessorKey: "actions",
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        enablePinning: true,
        pinned: "right",
        size: 120,
        Cell: ({ row }) => (
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="btn btn-with-icon-and-tooltip"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Edit Franchise"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.original.id);
              }}
            >
              <i className="text-primary fs-8 fas fa-pencil-alt"></i>
            </button>
            <button
              type="button"
              className="btn btn-with-icon-and-tooltip"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Delete Franchise"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original.id);
              }}
            >
              <i className="text-danger fs-8 fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    [columnOrder, tablePreferences, filterModes, columnSizing]
  );

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(franchisesData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Franchises");
    XLSX.writeFile(workbook, "franchises_list.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF("landscape", "pt", "A4");

    const columns = columnOrder
      .filter((key) => key !== "mrt-row-spacer" && key !== "actions")
      .map((key) => ({
        header: columnLabels[key] || key,
        dataKey: key,
      }));

    const rows = franchisesData.map((row) => {
      const filteredRow = {};
      columns.forEach((col) => {
        filteredRow[col.dataKey] = row[col.dataKey] ?? "";
      });
      return filteredRow;
    });

    doc.setFontSize(14);
    doc.text("Franchises List", 40, 40);

    doc.autoTable({
      columns,
      body: rows,
      startY: 60,
      theme: "striped",
      styles: {
        fontSize: 8,
        cellPadding: 4,
        overflow: "linebreak",
        minCellHeight: 20,
      },
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
        halign: "center",
      },
      bodyStyles: {
        halign: "left",
        valign: "middle",
      },
      margin: { top: 60, left: 40, right: 40 },
      tableWidth: "wrap",
    });

    doc.save("franchises_data.pdf");
  };

  return (
    <>
      <div className="page-header d-flex mb-3">
        <h2 className="page-heading fs-7">Franchises</h2>
      </div>

      <div className="card">
        <div className="card-body">
          <MaterialReactTable
            columns={columns}
            data={franchisesData}
            layoutMode="semantic"
            enableColumnFilters
            enableGlobalFilter
            enableColumnDragging
            enableColumnResizing
            enableHiding
            enableColumnOrdering
            enableSorting
            enableStickyHeader
            enableColumnFilterModes
            enableBottomToolbar={true}
            enablePagination={true}
            defaultColumn={{
              minSize: 50,
              maxSize: 1000,
              size: 150,
            }}
            initialState={{
              showColumnFilters: true,
              columnOrder,
              columnVisibility,
              sorting,
              columnSizing,
              columnFilters,
              density: "compact",
              columnPinning: { right: ["actions"] },
              pagination: { pageSize: 10, pageIndex: 0 },
            }}
            state={{
              columnOrder,
              columnVisibility,
              sorting,
              columnSizing,
              columnFilters,
            }}
            onColumnOrderChange={(newOrder) =>
              setColumnOrder(newOrder.filter((key) => key !== "mrt-row-spacer"))
            }
            onColumnVisibilityChange={setColumnVisibility}
            onSortingChange={(updater) => {
              const newSorting =
                typeof updater === "function" ? updater(sorting) : updater;
              setSorting(newSorting.slice(0, 1));
            }}
            onColumnSizingChange={(updater) => {
              const newSizing =
                typeof updater === "function" ? updater(columnSizing) : updater;
              setColumnSizing(
                Object.fromEntries(
                  Object.entries(newSizing).filter(
                    ([key]) => key !== "mrt-row-spacer"
                  )
                )
              );
            }}
            onColumnFiltersChange={(updater) => {
              const newFilters =
                typeof updater === "function"
                  ? updater(columnFilters)
                  : updater;
              setColumnFilters(
                newFilters.filter((filter) => filter.id !== "mrt-row-spacer")
              );
              const newFilterModes = { ...filterModes };
              newFilters.forEach((filter) => {
                if (filter.filterFn && filter.id !== "mrt-row-spacer") {
                  newFilterModes[filter.id] = filter.filterFn;
                }
              });
              setFilterModes(newFilterModes);
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "500px",
              },
            }}
            muiTablePaperProps={{
              sx: {
                boxShadow: "none",
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleRowClick(row),
              sx: { cursor: "pointer" },
            })}
            muiTableProps={{
              className: "custom-mrt-table",
              sx: {
                tableLayout: "auto",
                minWidth: "100%",
              },
            }}
            muiTableHeadCellProps={{
              className: "custom-mrt-table-head-cell",
            }}
            muiTableBodyCellProps={{
              className: "custom-mrt-table-body-cell",
            }}
            renderTopToolbarCustomActions={({ table }) => (
              <div className="d-flex gap-1 align-items-center">
                <button
                  type="button"
                  className="btn btn-with-icon-and-tooltip"
                  data-tooltip-id="global-tooltip"
                  data-tooltip-content="Add New Franchise"
                  onClick={handleAdd}
                >
                  <i className="text-info fs-7 fas fa-circle-plus"></i>
                </button>
                {/* <button
                  type="button"
                  className="btn btn-with-icon-and-tooltip"
                  data-tooltip-id="global-tooltip"
                  data-tooltip-content="Save Preferences"
                  onClick={saveTablePreferences}
                >
                  <i className="text-primary fs-7 fas fa-save"></i>
                </button> */}
                <button
                  type="button"
                  className="btn btn-with-icon-and-tooltip"
                  data-tooltip-id="global-tooltip"
                  data-tooltip-content="Export to Excel"
                  onClick={exportToExcel}
                >
                  <i className="text-success fs-7 fas fa-file-excel"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-with-icon-and-tooltip"
                  data-tooltip-id="global-tooltip"
                  data-tooltip-content="Export to PDF"
                  onClick={exportToPDF}
                >
                  <i className="text-danger fs-7 fas fa-file-pdf"></i>
                </button>
              </div>
            )}
          />
        </div>
      </div>

      {loading && <FullScreenLoader />}
    </>
  );
}

export default FranchiseList;
